/**
 * # entityRecordIdentity — the OWN group-5 records an ENTITY owns besides its `id`
 *
 * A DXF entity's own group-5 handle is its `id`, and the reducer that clones an
 * entity mints a fresh one for the clone. Several entities, however, are
 * COMPLEX ENTITIES in the DXF sense: the file emits further records INSIDE
 * them, each with a group-5 of its own that the parent carries as model data —
 *
 *   - an `INSERT` with attributes → the `ATTRIB` children (`attributes[].handle`)
 *     and the terminating `SEQEND` (`seqendHandle`);
 *   - a heavy `POLYLINE` → every `VERTEX` record (`vertices[].handle`) and the
 *     terminating `SEQEND` (`seqendHandle`);
 *   - an `ATTDEF` block child → its own group-5 lives in `handle`, not in `id`.
 *
 * A plain `{ ...entity, id }` spread therefore produces a clone that SHARES its
 * source's child handles, and two records with the same group-5 is a corrupt
 * file (strict readers reject it; ezdxf silently re-mints, so the corruption is
 * invisible until another application opens the drawing). The defect the T9b
 * review round-3 fix closed for `INSERT` (`cloneEntityWithFreshIdentity`) and
 * that PR #785 round 3 (P1) re-found for `POLYLINE` is the SAME defect: the
 * previous fix enumerated the ONE type it knew about, so the next complex
 * entity had to be remembered by hand — and was not.
 *
 * ## Why this module exists rather than another `case "Polyline"`
 *
 * {@link ownsNestedRecordIdentity} and {@link resetNestedRecordIdentity} are
 * keyed off the STRUCTURE — "does this entity carry a nested own group-5?" —
 * not off a type list, using the SAME three rules
 * `dxfBridge.conformantSkeleton.ts`'s `collectTypedOwnHandles` census uses to
 * decide what an own group-5 IS:
 *
 *   1. the own-handle KEYS are {@link OWN_HANDLE_KEYS} (`handle`,
 *      `seqendHandle`, `endblkHandle`) — every other `*Handle` / `*Handles` key
 *      is a REFERENCE and must never be blanked;
 *   2. a `{code: number, handle: string}` entry is a structural REFERENCE
 *      carrier (the viewport `layerOverrideHandles` 335/343/344/91 pointers),
 *      not an own handle — discriminated by the numeric `code` sibling;
 *   3. a raw `{entityType, groups}` record is verbatim byte passthrough whose
 *      own 5/105 lives inside the group stream — left alone.
 *
 * So a future complex entity that stores its children's handles under the same
 * `handle` key is covered the day it is added, with nothing to remember.
 *
 * {@link emitsTerminatingSeqend} is the one place where a TYPE LIST is
 * unavoidable and therefore deliberate: whether a `SEQEND` is emitted at all is
 * a property of the DXF grammar of that entity (an attribute-less INSERT emits
 * none; a heavy POLYLINE always emits one), and no field on the entity encodes
 * it. It is declared ONCE here and read by every site that mints, claims or
 * resets that terminator's handle — the from-scratch skeleton
 * (`dxfBridge.conformantSkeleton.ts`), the imported-document export preparation
 * (`dxfBridge.newRecordHandles.ts`) and the reducer clone helper
 * (`apps/web/src/app/state/geometryReducerHelpers.ts`) — so the three can never
 * disagree about which entities own a SEQEND.
 *
 * Source of truth: Autodesk DXF reference (R2018) — "Complex entity" /
 * POLYLINE-VERTEX-SEQEND and INSERT-ATTRIB-SEQEND layouts; ezdxf 1.4.4
 * (`Polyline.new_seqend`, `Insert.add_attrib`).
 */
import { OWN_HANDLE_KEYS } from "./dxfBridge.conformantSkeleton.handles";

/** An object node of the entity graph, keyed for the own-handle walk. */
type Node = Record<string, unknown>;

/**
 * Walk every NESTED own group-5 slot of `entity` (module doc, rules 1-3),
 * calling `visit(owner, key)` for each. `true` from `visit` STOPS the walk (the
 * existence query), which is why the walker returns a boolean rather than
 * building a list.
 *
 * The entity's OWN `id` is deliberately not a slot: it is the top-level
 * group-5 the caller mints. A top-level `handle` (an ATTDEF's real group-5) IS
 * one — the file emits it from `handle`, not from `id`.
 */
function walkOwnHandleSlots(
  node: unknown,
  visit: (owner: Node, key: string) => boolean | void,
): boolean {
  if (node === null || typeof node !== "object") return false;
  // targeted-cad-mutation-testing EQUIVALENT MUTANTS: `ConditionalExpression`
  // → false and `BlockStatement` → `{}` on the array fast path below. An array
  // that falls through to the generic branch is walked by `Object.entries`
  // under its INDEX keys ("0", "1", …) — none of which is in
  // `OWN_HANDLE_KEYS` — and each element object is then recursed into by the
  // same `typeof value === "object"` arm, which propagates the early STOP
  // identically. The fast path is a readability/allocation shortcut over a
  // traversal that is already correct, so neither mutant can change a result.
  // (The `return true` INSIDE it is NOT of this kind — it is the early-stop
  // propagation, killed by "sees a nested own handle that lives ONLY inside an
  // ARRAY".)
  if (Array.isArray(node)) {
    for (const item of node) {
      if (walkOwnHandleSlots(item, visit)) return true;
    }
    return false;
  }
  const record = node as Node;
  // Rule 2 — a `{code, handle}` REFERENCE carrier is not an own group-5, and it
  // nests no own-handle field, so the whole entry is skipped (the same early
  // return `collectTypedOwnHandles` makes for the same discriminant).
  if (typeof record.code === "number" && typeof record.handle === "string") {
    return false;
  }
  // Rule 3 — a raw `{entityType, groups}` record is verbatim passthrough whose
  // own 5 / 105 lives inside the group stream this walk must not rewrite.
  if (typeof record.entityType === "string" && Array.isArray(record.groups)) {
    return false;
  }
  for (const [key, value] of Object.entries(record)) {
    if (OWN_HANDLE_KEYS.has(key) && typeof value === "string") {
      if (visit(record, key) === true) return true;
      continue;
    }
    // targeted-cad-mutation-testing EQUIVALENT MUTANTS: every mutation of this
    // guard (`&&` → `||`, and `ConditionalExpression` → true on the whole
    // condition or on either operand). All four only ever WIDEN it, and the
    // widening is absorbed by this function's OWN first line: `null` and every
    // non-object recurse one level and return `false` immediately. The guard
    // exists to skip a pointless call, not to decide anything — so nothing
    // narrower than "does the recursion terminate" is observable, and that is
    // pinned by the non-object cases of `ownsNestedRecordIdentity` above.
    if (value !== null && typeof value === "object") {
      if (walkOwnHandleSlots(value, visit)) return true;
    }
  }
  return false;
}

/**
 * `true` when `entity` carries at least one NESTED own group-5 handle (an
 * `ATTRIB` / `VERTEX` child handle, an `ATTDEF`'s own `handle`, a terminating
 * `SEQEND`'s `seqendHandle`) — i.e. when a plain spread clone of it would
 * duplicate a record identity, and when the export must visit it to claim /
 * remint those handles rather than passing it through by reference.
 *
 * Structural (module doc): no entity type is named.
 */
export function ownsNestedRecordIdentity(entity: unknown): boolean {
  return walkOwnHandleSlots(entity, () => true);
}

/**
 * `entity` with every NESTED own group-5 handle RESET, so the clone owns no
 * record identity of its source's. PURE — the source is never mutated.
 *
 * A `handle` slot is reset to `""` (the schema default of every record that
 * carries one: `polylineVertexRecordSchema`, `blockInsertAttributeSchema`,
 * `attdefEntitySchema`), which is exactly what a freshly authored child looks
 * like and what the export's minter mints from. Any other own-handle key
 * (`seqendHandle`, `endblkHandle`) is OPTIONAL in the model, so it is DELETED
 * rather than emptied — an explicit `""` there would be a value the schema
 * never produces and that `lacksOwnHandle` would then have to special-case.
 */
export function resetNestedRecordIdentity<T>(entity: T): T {
  if (!ownsNestedRecordIdentity(entity)) return entity;
  const clone = structuredClone(entity);
  walkOwnHandleSlots(clone, (owner, key) => {
    if (key === "handle") owner[key] = "";
    else delete owner[key];
  });
  return clone;
}

/**
 * `true` when the DXF export emits a terminating `SEQEND` for `entity`, whose
 * own group-5 is the entity's `seqendHandle`.
 *
 * THE deliberate type list of this module (module doc): a heavy `POLYLINE` is
 * always written `POLYLINE … VERTEX* SEQEND` (`dxf-io/writer.ts` emits the
 * terminator unconditionally, per the Autodesk DXF reference's complex-entity
 * layout and ezdxf's `Polyline.new_seqend`), while an `INSERT` is written
 * `INSERT ATTRIB* SEQEND` ONLY when it actually carries attributes — an
 * attribute-less INSERT emits no SEQEND, so it owns no such handle.
 */
export function emitsTerminatingSeqend(entity: unknown): boolean {
  if (entity === null || typeof entity !== "object") return false;
  const record = entity as { type?: unknown; attributes?: unknown };
  if (record.type === "Polyline") return true;
  return (
    record.type === "Block" &&
    Array.isArray(record.attributes) &&
    record.attributes.length > 0
  );
}
